// ink-component
import React, { MouseEvent, useState } from 'react';

import { ThemeProvider } from 'styled-components';
import ModalDetailSidebar from './ModalDetailSidebar/ModalDetailSidebar';
import { SidebarItem } from './ModalDetailSidebarItem/ModalDetailSidebarItem';

import getGlobalProps from '../../helpers/constants/global-props';
import { withModalContext } from '../../contexts/ModalContext';

import { ModalDetailWrapper, ModalDetailContent } from './styles';
import useWindowWidth from '../../hooks/useWindowWidth';

export interface ModalDetailProps {
  /** Small tag on top of the title */
  tag?: string;
  /** Main title of the header */
  title?: string;
  /** List of tabs and matching contents of each tab */
  sidebarItems: SidebarItem[];
  /** Active tab index */
  active?: number;
  /** Text of the button inside the header */
  actionText?: string;
  /** Optional action callback */
  actionCallback?: (event: MouseEvent<HTMLButtonElement>) => void;
  /** Optional onChange callback */
  onChange(nextActive: SidebarItem, index: number, event: MouseEvent): void;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** @ignore */
  hideModal?: () => void;
}

const globalProps = getGlobalProps({});

/**
 * @purpose Displays a detailed view with multiple tabs
 * @group Modal
 * @categories layout
 */
function ModalDetail({ active = 0, 'data-testid': dataTestId, ...props }: ModalDetailProps) {
  const [activeState, setActiveState] = useState<number>(active);

  function handleClick(nextActive: SidebarItem, index: number, e: MouseEvent) {
    props.onChange(nextActive, index, e);

    setActiveState(index);
  }

  function isActive(index: number) {
    return activeState === index;
  }

  function renderActiveContent() {
    return props.sidebarItems[activeState] && props.sidebarItems[activeState].content;
  }

  const { isMobile } = useWindowWidth();

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        modalDetailMobile: isMobile,
      })}
    >
      <ModalDetailWrapper {...globalProps} data-testid={dataTestId}>
        <ModalDetailSidebar
          tag={props.tag}
          title={props.title}
          actionText={props.actionText}
          actionCallback={props.actionCallback}
          isActive={isActive}
          onClick={handleClick}
          hideModal={() => props.hideModal && props.hideModal()}
          {...props}
        />
        <ModalDetailContent>{renderActiveContent()}</ModalDetailContent>
      </ModalDetailWrapper>
    </ThemeProvider>
  );
}

ModalDetail.defaultProps = {
  tag: null,
  title: null,
  active: 0,
  actionText: null,
  actionCallback: (_event: MouseEvent) => {},
  onChange: (_nextActive: SidebarItem, _index: number, _event: MouseEvent) => {},
  'data-testid': undefined,
};

ModalDetail.displayName = 'ModalDetail';

export { ModalDetail as ModalDetailDefault };

export default withModalContext(ModalDetail);
